/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AuthApiService } from '../../core/http';
import { BaseComponent } from '../../core/components';
import { CheckoutService, DashboardService } from './shared/services/component';
import { Component } from '@angular/core';
import { DashboardDataModel } from './shared/models';
import { ErrorResponseModel, MenuConfigModel, MenuItemsModel } from '../../core/models';
import { KeyStoreEnum } from '../../core/enums';
import {
    KeyStoreService,
    LogService,
    ModalService,
    RbacService,
    SocketService,
    ThemeService,
    UserService,
    UtilsService
} from '../../core/services';
import { NamespaceService, PortalRbacService } from './shared/services/utils';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';

declare let Intercom: any;

@Component({
    selector: 'app-centralised-management',
    templateUrl: './centralisedManagement.component.html',
    styleUrls: ['./centralisedManagement.component.scss']
})

export class CentralisedManagementComponent extends BaseComponent {
    /**
     * Menu config.
     */
    public menuConfig: MenuConfigModel;

    /**
     * To store the dropdowns.
     */
    public dropdown: any = {
        namespaces: [],
        products: []
    };

    /**
     * Get the number of adc to determine the location of the services link.
     */
    public showAdcListPage: boolean = false;

    /**
     * Get the number of schedules to determine the location of the services link.
     */
    public showSchedulesListPage: boolean = false;

    /**
     * Get the number of backups to determine the location of the services link.
     */
    public showBackupListPage: boolean = false;

    /**
     * Get the number of backups to determine the location of the services link.
     */
    public showInsightListPage: boolean = false;

    /**
     * Get the number of certificates to determine the location of the services link.
     */
    public showCertsListPage: boolean = false;

    /**
     * Set if the tier is free.
     */
    public tierIsFree: boolean = false;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Store the portal visible links.
     */
    public myPortalLinkLookup: any = {};

    /**
     * Service dropdown disabled.
     */
    public servicesDropdownDisabled: boolean = false;

    /**
     * User has access to security pages.
     */
    public canAccessSecurityPages: boolean = false;

    /**
     * Cart total items
     */
    public cartTotal: number = 0;

    /**
     * Hide cart icon on checkout page.
     */
    public showCart: boolean = true;

    constructor(
        private authApiService: AuthApiService,
        private checkoutService: CheckoutService,
        private dashboardService: DashboardService,
        private keyStoreService: KeyStoreService,
        private logService: LogService,
        private modalService: ModalService,
        private namespaceService: NamespaceService,
        private portalRbacService: PortalRbacService,
        private rbacService: RbacService,
        private router: Router,
        private socketService: SocketService,
        private themeService: ThemeService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpBurgerMenu();

        this.keyStoreService.storeState = KeyStoreEnum.OPEN;
        this.themeService.setTheme('default');
        this.tierIsFree = this.portalRbacService.selectedTier?.tier === 'free';

        this.setupDashboardData();
        this.setUpCartView();
        this.registerButtons();
        this.registerMyPortalLinks();
        this.canAccessSecurityPages = this.hasAccessToSecurity();
    }

    /**
     * Link to documentation page.
     */
    public externalDocumentationLink(): void {
        const resourcesMenuConfig: MenuConfigModel = this.rbacService.getMenuById('resources');
        const documentationMenuConfig: MenuItemsModel = resourcesMenuConfig.menuItems.find(
            (item: MenuItemsModel): boolean => item.id === 'resources-documentation'
        );
        window.open(documentationMenuConfig.location, '_blank');
    }

    /**
     * Link to status page.
     */
    public externalStatusLink(): void {
        window.open('https://status.loadbalancer.org/', '_blank');
    }

    /**
     * Get router link
     */
    public getRouterLink(): string {
        const securityPerms: any = this.getSecurityPermissions();

        if (securityPerms.insights && securityPerms.certs) {
            return this.getFullPermissionsRoute();
        } else if (securityPerms.insights && !securityPerms.certs) {
            return 'security/insights';
        } else if (!securityPerms.insights && securityPerms.certs) {
            return 'security/certificates';
        }
    }

    /**
     * Get first route if user has full security permissions.
     * @private
     */
    private getFullPermissionsRoute(): string {
        if (this.showInsightListPage) {
            return 'security/insights';
        } else if (this.showCertsListPage) {
            return 'security/certificates';
        } else {
            return 'security/overview';
        }
    }

    /**
     * Logout helper function to open modal and close burger menu.
     */
    public openLogoutModal(): void {
        this.modalService.openModalInParent('logoutConfirm');
    }

    /**
     * Logout of the user interface
     */
    public logout(): void {
        this.modalService.closeAll();
        this.namespaceService.resetService();
        this.keyStoreService.shutdown();
        this.userService.logout();
        this.socketService.close();

        this.authApiService.invalidateJWT()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {

                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'CentralisedManagementComponent: logout()',
                        'Failed to invalidate token - ' + error.message
                    );
                }
            });
    }

    /**
     * Bring up the Intercom panel with news showing.
     */
    public getNews(): void {
        Intercom('showSpace', 'news');
    }

    /**
     * Bring up the Intercom to give us feedback.
     */
    public giveFeedback(): void {
        Intercom('startSurvey', 25895471);
    }

    /**
     * Check on user access to security pages.
     * @private
     */
    private hasAccessToSecurity(): boolean {
        const securityPerms = this.getSecurityPermissions();
        if (!this.tierIsFree) {
            return securityPerms.insights || securityPerms.certs;
        } else {
            return securityPerms.insights;
        }
    }

    /**
     * Set up burger menu
     * @private
     */
    private setUpBurgerMenu(): void {
        const menus: any = this.utilsService.duplicateObject(this.rbacService.menus);
        const burgerMenu = { menuTitle: '', menuSubTitle: '', menuItems: [] };

        for (const menu of menus) {
            const filteredMenuItems = menu.menuItems;
            const index: number = filteredMenuItems.findIndex((item: MenuItemsModel): boolean => item.children?.length > 0);
            if (index !== -1) {
                filteredMenuItems[index].isChild = true;
            }

            const menuItem = {
                location: '',
                heading: menu.menuTitle,
                id: menu.menuTitle,
                iconLocation: menu.menuTitleImage,
                hidden: false,
                children: filteredMenuItems
            };

            burgerMenu.menuItems.push(menuItem);
        }

        this.menuConfig = burgerMenu;
    }

    /**
     * Subscribe to service changes to keep counts up to date.
     * @private
     */
    private setupDashboardData(): void {
        const errors: any = this.dashboardService.errors;
        const allData: DashboardDataModel = this.dashboardService.allData;
        this.setPageToggles(allData, errors);

        this.dashboardService.allDataEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.setPageToggles(data, errors);
                }
            });
    }

    /**
     * Set page toggles for each menu item.
     * @param data
     * @param errors
     * @private
     */
    private setPageToggles(
        data: DashboardDataModel,
        errors: {
            adcs: any;
            backups: any;
            certs: any;
            insights: any;
            schedules: any;
        }
    ): void {
        this.showAdcListPage = data.adcs?.length > 1 || !!errors?.adcs;
        this.showBackupListPage = data.backups?.length > 1 || !!errors?.backups;
        this.showCertsListPage = data.certs?.length > 1 || !!errors?.certs;
        this.showInsightListPage = data.insights?.length > 1 || !!errors?.insights;
        this.showSchedulesListPage = data.schedules?.length > 1 || !!errors?.schedules;
    }

    /**
     * Set up cart view.
     * @private
     */
    private setUpCartView(): void {
        this.cartTotal = this.checkoutService.cartItemsCount;
        this.checkoutService.cartItemsEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (total: number): void => {
                    this.cartTotal = total;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.warn('CentralisedManagementComponent: setUpCartTotal', 'Unable to get cart total.', error.message);
                }
            });

        this.router.events
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.showCart = !this.router.url.includes('/checkout');
                }
            });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'central-adc-list-link': [{ path: '/api/v1/:namespace/adcs/', methods: ['GET'] }],
            'central-schedule-list-link': [{ path: '/api/v1/:namespace/scheduler/', methods: ['GET'] }],
            'central-storage-list-link': [{ path: '/api/v1/:namespace/backups/', methods: ['GET'] }],
            'central-security-list-link': [{ path: '/api/v1/:namespace/adcs/', methods: ['GET'] }],
            'central-support-list-link': [{ path: '/api/v1/support/tickets/', methods: ['GET'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
        const servicesDropdown: any = this.utilsService.duplicateObject(this.permissionsLookup);
        delete servicesDropdown['central-support-list-link']; // support isn't part of service dropdown.
        const values: boolean[] = Object.values(servicesDropdown);
        this.servicesDropdownDisabled = values.every((item: boolean): boolean => !item);
    }

    /**
     * Get user permissions for security page access.
     * @private
     */
    private getSecurityPermissions(): any {
        return this.rbacService.getPermissionsLookup({
            'insights': [{ path: '/api/v1/:namespace/security/insights', methods: ['GET'] }],
            'certs': [{ path: '/api/v1/:namespace/security/certificates', methods: ['GET'] }],
        });
    }

    /**
     * Register my portal links
     * @private
     */
    private registerMyPortalLinks(): void {
        const accountsMenu: MenuConfigModel = this.rbacService.getMenuById('accounts');
        this.myPortalLinkLookup['cm/accounts'] = this.getFirstVisibleItemLocation(accountsMenu, 'cm/accounts');

        const organisationMenu: MenuConfigModel = this.rbacService.getMenuById('organisation');
        this.myPortalLinkLookup['/cm/organisation'] = this.getFirstVisibleItemLocation(organisationMenu, '/cm/organisation');
    }

    /**
     *
     * @param menu
     * @param parentLocation
     * @private
     */
    private getFirstVisibleItemLocation(menu: MenuConfigModel, parentLocation: string): string {
        const firstItem: MenuItemsModel = menu.menuItems.find((item: MenuItemsModel): boolean => !item.hidden);
        if (firstItem.location) {
            return firstItem.location;
        } else {
            this.logService.warn('CentralisedManagementComponent: getFirstVisibleItemLocation', 'Unable to find visible menu item');

            return parentLocation;
        }
    }
}
